var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import classNames from 'classnames';
import toArray from 'rc-util/lib/Children/toArray';
import copy from 'copy-to-clipboard';
import omit from 'rc-util/lib/omit';
import EditOutlined from '@ant-design/icons/EditOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import ResizeObserver from 'rc-resize-observer';
import { configConsumerProps, ConfigContext } from '../config-provider';
import LocaleReceiver from '../locale-provider/LocaleReceiver';
import devWarning from '../_util/devWarning';
import TransButton from '../_util/transButton';
import raf from '../_util/raf';
import { isStyleSupport } from '../_util/styleChecker';
import Tooltip from '../tooltip';
import Typography from './Typography';
import Editable from './Editable';
import measure from './util';
const isLineClampSupport = isStyleSupport('webkitLineClamp');
const isTextOverflowSupport = isStyleSupport('textOverflow');
function wrapperDecorations({ mark, code, underline, delete: del, strong, keyboard }, content) {
    let currentContent = content;
    function wrap(needed, tag) {
        if (!needed)
            return;
        currentContent = React.createElement(tag, {}, currentContent);
    }
    wrap(strong, 'strong');
    wrap(underline, 'u');
    wrap(del, 'del');
    wrap(code, 'code');
    wrap(mark, 'mark');
    wrap(keyboard, 'kbd');
    return currentContent;
}
const ELLIPSIS_STR = '...';
class Base extends React.Component {
    constructor() {
        super(...arguments);
        this.contentRef = React.createRef();
        this.state = {
            edit: false,
            copied: false,
            ellipsisText: '',
            ellipsisContent: null,
            isEllipsis: false,
            expanded: false,
            clientRendered: false,
        };
        this.getPrefixCls = () => {
            const { prefixCls: customizePrefixCls } = this.props;
            const { getPrefixCls } = this.context;
            return getPrefixCls('typography', customizePrefixCls);
        };
        // =============== Expand ===============
        this.onExpandClick = e => {
            var _a;
            const { onExpand } = this.getEllipsis();
            this.setState({ expanded: true });
            (_a = onExpand) === null || _a === void 0 ? void 0 : _a(e);
        };
        // ================ Edit ================
        this.onEditClick = () => {
            this.triggerEdit(true);
        };
        this.onEditChange = (value) => {
            const { onChange } = this.getEditable();
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
            this.triggerEdit(false);
        };
        this.onEditCancel = () => {
            var _a, _b;
            (_b = (_a = this.getEditable()).onCancel) === null || _b === void 0 ? void 0 : _b.call(_a);
            this.triggerEdit(false);
        };
        // ================ Copy ================
        this.onCopyClick = (e) => {
            e.preventDefault();
            const { children, copyable } = this.props;
            const copyConfig = Object.assign({}, (typeof copyable === 'object' ? copyable : null));
            if (copyConfig.text === undefined) {
                copyConfig.text = String(children);
            }
            copy(copyConfig.text || '');
            this.setState({ copied: true }, () => {
                if (copyConfig.onCopy) {
                    copyConfig.onCopy();
                }
                this.copyId = window.setTimeout(() => {
                    this.setState({ copied: false });
                }, 3000);
            });
        };
        this.setEditRef = (node) => {
            this.editIcon = node;
        };
        this.triggerEdit = (edit) => {
            const { onStart } = this.getEditable();
            if (edit && onStart) {
                onStart();
            }
            this.setState({ edit }, () => {
                if (!edit && this.editIcon) {
                    this.editIcon.focus();
                }
            });
        };
        // ============== Ellipsis ==============
        this.resizeOnNextFrame = () => {
            raf.cancel(this.rafId);
            this.rafId = raf(() => {
                // Do not bind `syncEllipsis`. It need for test usage on prototype
                this.syncEllipsis();
            });
        };
    }
    static getDerivedStateFromProps(nextProps) {
        const { children, editable } = nextProps;
        if (process.env.NODE_ENV !== 'production')
            devWarning(!editable || typeof children === 'string', 'Typography', 'When `editable` is enabled, the `children` should use string.');
        return {};
    }
    componentDidMount() {
        this.setState({ clientRendered: true });
        this.resizeOnNextFrame();
    }
    componentDidUpdate(prevProps) {
        const { children } = this.props;
        const ellipsis = this.getEllipsis();
        const prevEllipsis = this.getEllipsis(prevProps);
        if (children !== prevProps.children || ellipsis.rows !== prevEllipsis.rows) {
            this.resizeOnNextFrame();
        }
    }
    componentWillUnmount() {
        window.clearTimeout(this.copyId);
        raf.cancel(this.rafId);
    }
    getEditable(props) {
        const { edit } = this.state;
        const { editable } = props || this.props;
        if (!editable)
            return { editing: edit };
        return Object.assign({ editing: edit }, (typeof editable === 'object' ? editable : null));
    }
    getEllipsis(props) {
        const { ellipsis } = props || this.props;
        if (!ellipsis)
            return {};
        return Object.assign({ rows: 1, expandable: false }, (typeof ellipsis === 'object' ? ellipsis : null));
    }
    canUseCSSEllipsis() {
        const { clientRendered } = this.state;
        const { editable, copyable } = this.props;
        const { rows, expandable, suffix, onEllipsis, tooltip } = this.getEllipsis();
        if (suffix || tooltip)
            return false;
        // Can't use css ellipsis since we need to provide the place for button
        if (editable || copyable || expandable || !clientRendered || onEllipsis) {
            return false;
        }
        if (rows === 1) {
            return isTextOverflowSupport;
        }
        return isLineClampSupport;
    }
    syncEllipsis() {
        const { ellipsisText, isEllipsis, expanded } = this.state;
        const { rows, suffix, onEllipsis } = this.getEllipsis();
        const { children } = this.props;
        if (!rows || rows < 0 || !this.contentRef.current || expanded)
            return;
        // Do not measure if css already support ellipsis
        if (this.canUseCSSEllipsis())
            return;
        devWarning(toArray(children).every((child) => typeof child === 'string'), 'Typography', '`ellipsis` should use string as children only.');
        const { content, text, ellipsis } = measure(this.contentRef.current, { rows, suffix }, children, this.renderOperations(true), ELLIPSIS_STR);
        if (ellipsisText !== text || isEllipsis !== ellipsis) {
            this.setState({ ellipsisText: text, ellipsisContent: content, isEllipsis: ellipsis });
            if (isEllipsis !== ellipsis && onEllipsis) {
                onEllipsis(ellipsis);
            }
        }
    }
    renderExpand(forceRender) {
        const { expandable, symbol } = this.getEllipsis();
        const { expanded, isEllipsis } = this.state;
        if (!expandable)
            return null;
        // force render expand icon for measure usage or it will cause dead loop
        if (!forceRender && (expanded || !isEllipsis))
            return null;
        let expandContent;
        if (symbol) {
            expandContent = symbol;
        }
        else {
            expandContent = this.expandStr;
        }
        return (<a key="expand" className={`${this.getPrefixCls()}-expand`} onClick={this.onExpandClick} aria-label={this.expandStr}>
        {expandContent}
      </a>);
    }
    renderEdit() {
        const { editable } = this.props;
        if (!editable)
            return;
        const { icon, tooltip } = editable;
        const title = toArray(tooltip)[0] || this.editStr;
        const ariaLabel = typeof title === 'string' ? title : '';
        return (<Tooltip key="edit" title={tooltip === false ? '' : title}>
        <TransButton ref={this.setEditRef} className={`${this.getPrefixCls()}-edit`} onClick={this.onEditClick} aria-label={ariaLabel}>
          {icon || <EditOutlined role="button"/>}
        </TransButton>
      </Tooltip>);
    }
    renderCopy() {
        const { copied } = this.state;
        const { copyable } = this.props;
        if (!copyable)
            return;
        const prefixCls = this.getPrefixCls();
        const { tooltips } = copyable;
        let tooltipNodes = toArray(tooltips);
        if (tooltipNodes.length === 0) {
            tooltipNodes = [this.copyStr, this.copiedStr];
        }
        const title = copied ? tooltipNodes[1] : tooltipNodes[0];
        const ariaLabel = typeof title === 'string' ? title : '';
        const icons = toArray(copyable.icon);
        return (<Tooltip key="copy" title={tooltips === false ? '' : title}>
        <TransButton className={classNames(`${prefixCls}-copy`, copied && `${prefixCls}-copy-success`)} onClick={this.onCopyClick} aria-label={ariaLabel}>
          {copied ? icons[1] || <CheckOutlined /> : icons[0] || <CopyOutlined />}
        </TransButton>
      </Tooltip>);
    }
    renderEditInput() {
        const { children, className, style } = this.props;
        const { direction } = this.context;
        const { maxLength, autoSize, onEnd } = this.getEditable();
        return (<Editable value={typeof children === 'string' ? children : ''} onSave={this.onEditChange} onCancel={this.onEditCancel} onEnd={onEnd} prefixCls={this.getPrefixCls()} className={className} style={style} direction={direction} maxLength={maxLength} autoSize={autoSize}/>);
    }
    renderOperations(forceRenderExpanded) {
        return [this.renderExpand(forceRenderExpanded), this.renderEdit(), this.renderCopy()].filter(node => node);
    }
    renderContent() {
        const { ellipsisContent, isEllipsis, expanded } = this.state;
        const _a = this.props, { component, children, className, type, disabled, style } = _a, restProps = __rest(_a, ["component", "children", "className", "type", "disabled", "style"]);
        const { direction } = this.context;
        const { rows, suffix, tooltip } = this.getEllipsis();
        const prefixCls = this.getPrefixCls();
        const textProps = omit(restProps, [
            'prefixCls',
            'editable',
            'copyable',
            'ellipsis',
            'mark',
            'code',
            'delete',
            'underline',
            'strong',
            'keyboard',
            ...configConsumerProps,
        ]);
        const cssEllipsis = this.canUseCSSEllipsis();
        const cssTextOverflow = rows === 1 && cssEllipsis;
        const cssLineClamp = rows && rows > 1 && cssEllipsis;
        let textNode = children;
        // Only use js ellipsis when css ellipsis not support
        if (rows && isEllipsis && !expanded && !cssEllipsis) {
            const { title } = restProps;
            let restContent = title || '';
            if (!title && (typeof children === 'string' || typeof children === 'number')) {
                restContent = String(children);
            }
            // show rest content as title on symbol
            restContent = restContent === null || restContent === void 0 ? void 0 : restContent.slice(String(ellipsisContent || '').length);
            // We move full content to outer element to avoid repeat read the content by accessibility
            textNode = (<>
          {ellipsisContent}
          <span title={restContent} aria-hidden="true">
            {ELLIPSIS_STR}
          </span>
          {suffix}
        </>);
            // If provided tooltip, we need wrap with span to let Tooltip inject events
            if (tooltip) {
                textNode = (<Tooltip title={tooltip === true ? children : tooltip}>
            <span>{textNode}</span>
          </Tooltip>);
            }
        }
        else {
            textNode = (<>
          {children}
          {suffix}
        </>);
        }
        textNode = wrapperDecorations(this.props, textNode);
        return (<LocaleReceiver componentName="Text">
        {({ edit, copy: copyStr, copied, expand }) => {
                this.editStr = edit;
                this.copyStr = copyStr;
                this.copiedStr = copied;
                this.expandStr = expand;
                return (<ResizeObserver onResize={this.resizeOnNextFrame} disabled={!rows}>
              <Typography className={classNames({
                        [`${prefixCls}-${type}`]: type,
                        [`${prefixCls}-disabled`]: disabled,
                        [`${prefixCls}-ellipsis`]: rows,
                        [`${prefixCls}-ellipsis-single-line`]: cssTextOverflow,
                        [`${prefixCls}-ellipsis-multiple-line`]: cssLineClamp,
                    }, className)} style={Object.assign(Object.assign({}, style), { WebkitLineClamp: cssLineClamp ? rows : undefined })} component={component} ref={this.contentRef} direction={direction} {...textProps}>
                {textNode}
                {this.renderOperations()}
              </Typography>
            </ResizeObserver>);
            }}
      </LocaleReceiver>);
    }
    render() {
        const { editing } = this.getEditable();
        if (editing) {
            return this.renderEditInput();
        }
        return this.renderContent();
    }
}
Base.contextType = ConfigContext;
Base.defaultProps = {
    children: '',
};
export default Base;
